import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useDeals } from '../lib/api';
import type { DealDto } from '../types';
import { SourceLink, fmtDate, fmtRevenueUsdM } from './_shared';

// Tab — Deals (Phase C). Comparable transactions in the target universe's segments,
// discovered weekly by mna-deal-hunt. Sorted announced-date descending; EV renders
// confidence-aware (estimated → "(est.)" with the basis as tooltip, null → grey "n/d").
// The summary strip's median EBITDA multiple is computed client-side from disclosed
// deals only, and honestly shows "n/d" when fewer than 3 deals disclose a multiple.

const MEDIAN_MIN_DISCLOSED = 3;

const STATUS_STYLES: Record<DealDto['status'], string> = {
  announced: 'border-accent-info text-accent-info',
  closed: 'border-score-high text-score-high',
  terminated: 'border-border-strong text-muted',
};

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export default function Deals() {
  const { data, isLoading, error } = useDeals();

  const deals = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.announcedDate.localeCompare(a.announcedDate)),
    [data]
  );

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error) return <ErrorState message={(error as Error).message} />;

  const disclosedMultiples = deals
    .map((d) => d.ebitdaMultiple)
    .filter((m): m is number => m !== null && m !== undefined);
  const medianMultiple =
    disclosedMultiples.length >= MEDIAN_MIN_DISCLOSED ? median(disclosedMultiples) : null;

  // A multiples column where EVERY deal is n/d carries no information — hide it.
  const showRevMultiple = deals.some((d) => d.revenueMultiple !== null && d.revenueMultiple !== undefined);
  const showEbitdaMultiple = disclosedMultiples.length > 0;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Comparable transactions</CardTitle>
        <CardDescription className="mt-2">
          Announced and closed deals across the drilling-equipment value chain — pricing context
          for the target universe. Every deal links its source; estimated values carry their basis.
        </CardDescription>
      </Card>

      {deals.length === 0 ? (
        <Card>
          <CardDescription>
            No comparable transactions yet — mna-deal-hunt discovers them weekly.
          </CardDescription>
        </Card>
      ) : (
        <>
          {/* Summary strip */}
          <Card>
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <SummaryStat label="Transactions" value={String(deals.length)} />
              <SummaryStat
                label={`Median EBITDA multiple (${disclosedMultiples.length} disclosed)`}
                value={medianMultiple === null ? 'n/d' : `${medianMultiple.toFixed(1)}×`}
                muted={medianMultiple === null}
                title={
                  medianMultiple === null
                    ? `Needs at least ${MEDIAN_MIN_DISCLOSED} deals with a disclosed EBITDA multiple`
                    : undefined
                }
              />
            </div>
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-accent">
                    <Th>Announced</Th>
                    <Th>Acquirer</Th>
                    <Th>Target</Th>
                    <Th center>Status</Th>
                    <Th right>EV</Th>
                    {showRevMultiple && <Th right>Rev &times;</Th>}
                    {showEbitdaMultiple && <Th right>EBITDA &times;</Th>}
                    <Th center>Source</Th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((d) => (
                    <DealRow
                      key={d.id}
                      deal={d}
                      showRevMultiple={showRevMultiple}
                      showEbitdaMultiple={showEbitdaMultiple}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/* ---------- pieces ---------- */

function Th({ children, center, right }: { children: ReactNode; center?: boolean; right?: boolean }) {
  return (
    <th
      className={cn(
        'py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)',
        center ? 'text-center' : right ? 'text-right' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function SummaryStat({
  label,
  value,
  muted,
  title,
}: {
  label: string;
  value: string;
  muted?: boolean;
  title?: string;
}) {
  return (
    <div title={title}>
      <span
        className={cn(
          'font-(family-name:--font-heading) text-2xl font-bold [font-variant-numeric:tabular-nums]',
          muted ? 'text-muted' : 'text-text'
        )}
      >
        {value}
      </span>
      <span className="ml-2 font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
        {label}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: DealDto['status'] }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5',
        'font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]',
        STATUS_STYLES[status]
      )}
    >
      {status}
    </span>
  );
}

/** EV, confidence-aware: estimated → amber "(est.)" with the basis as tooltip; null → grey n/d. */
function EvCell({ deal: d }: { deal: DealDto }) {
  if (d.evUsdM === null || d.evUsdM === undefined) return <NotDisclosed />;
  const value = fmtRevenueUsdM(d.evUsdM);
  if (d.confidence === 'estimated') {
    return (
      <span className="text-amber-500" title={d.estBasis ?? undefined}>
        <span className="text-xs">(est.) </span>
        {value}
      </span>
    );
  }
  return <span>{value}</span>;
}

function MultipleCell({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <NotDisclosed />;
  return <span>{value.toFixed(1)}&times;</span>;
}

function NotDisclosed() {
  return (
    <span className="text-muted opacity-70" title="Not disclosed">
      n/d
    </span>
  );
}

function DealRow({
  deal: d,
  showRevMultiple,
  showEbitdaMultiple,
}: {
  deal: DealDto;
  showRevMultiple: boolean;
  showEbitdaMultiple: boolean;
}) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3 whitespace-nowrap [font-variant-numeric:tabular-nums]">
        {fmtDate(d.announcedDate)}
      </td>
      <td className="py-3 px-3">{d.acquirer}</td>
      <td className="py-3 px-3">
        {d.targetSlug ? (
          <Link
            to={`/mna/companies/${d.targetSlug}`}
            className="font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {d.target}
          </Link>
        ) : (
          <span className="font-medium text-text">{d.target}</span>
        )}
      </td>
      <td className="py-3 px-3 text-center">
        <StatusBadge status={d.status} />
      </td>
      <td className="py-3 px-3 text-right whitespace-nowrap [font-variant-numeric:tabular-nums]">
        <EvCell deal={d} />
      </td>
      {showRevMultiple && (
        <td className="py-3 px-3 text-right whitespace-nowrap [font-variant-numeric:tabular-nums]">
          <MultipleCell value={d.revenueMultiple} />
        </td>
      )}
      {showEbitdaMultiple && (
        <td className="py-3 px-3 text-right whitespace-nowrap [font-variant-numeric:tabular-nums]">
          <MultipleCell value={d.ebitdaMultiple} />
        </td>
      )}
      <td className="py-3 px-3 text-center">
        {d.source ? <SourceLink source={d.source} /> : <span className="text-muted">—</span>}
      </td>
    </tr>
  );
}
